import React, { useMemo } from 'react'
import styled from 'styled-components'
import { PairImage } from './PairImage'
import { RightChevron } from '../icons'
import { usePreferredCurrency } from '../../hooks'
import { Colors, FontWeights, PairsItem } from '../../constants'
import { useApiPools } from '../../providers'
import { bigNumber18DecimalsToDollars, bigNumber18DecimalsToPercent } from '../../utils'

interface CurrencySelectItemProps {
  pair: PairsItem
  onClick: () => void
}

export function PairSelectItem({ pair, onClick }: CurrencySelectItemProps) {
  const apiPools = useApiPools()

  const [currency, other] = pair.tokens
  const [currency0] = usePreferredCurrency(currency)
  const [currency1] = usePreferredCurrency(other)

  const { aprDisplay, potentialAprDisplay, tvlDisplay } = useMemo(() => {
    let aprDisplay = '-'
    let potentialAprDisplay = ''
    let tvlDisplay = '-'
    if (apiPools) {
      const apiPoolData = apiPools[pair.address.toLocaleLowerCase()]
      if (apiPoolData) {
        aprDisplay = bigNumber18DecimalsToPercent(apiPoolData.apr)
        if (!apiPoolData.apr.eq(apiPoolData.potentialApr)) {
          potentialAprDisplay = `(Restore ${bigNumber18DecimalsToPercent(apiPoolData.potentialApr)})`
        }
        tvlDisplay = bigNumber18DecimalsToDollars(apiPoolData.totalTokenValue)
      }
    }
    return {
      aprDisplay,
      potentialAprDisplay,
      tvlDisplay,
    }
  }, [apiPools, pair])

  return (
    <Button onClick={onClick}>
      <Pair>
        <PairImage currencySymbol={currency0.symbol} otherSymbol={currency1.symbol} />
        {currency0.symbol}-{currency1.symbol}
      </Pair>
      <Details>
        <li>
          <strong>APR</strong>
          <span>
            {aprDisplay}
            {potentialAprDisplay ? <small>{potentialAprDisplay}</small> : ''}
          </span>
        </li>
        <li>
          <strong>TVL</strong>
          <span>{tvlDisplay}</span>
        </li>
      </Details>
      <RightChevronStyled />
    </Button>
  )
}

const Button = styled.button`
  padding: 15px 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  background: none;
  border: none;
  outline: none;
  cursor: pointer;

  &:hover {
    color: ${Colors.Hover};
  }
`

const Pair = styled.div`
  display: flex;
  flex-basis: 40%;
  align-items: center;
  font-size: 15px;
  font-weight: bold;
`

const Details = styled.ul`
  padding: 0;
  margin: 0;
  flex-basis: 40%;
  font-size: 11px;
  text-align: left;
  list-style: none;

  strong {
    margin-right: 5px;
    color: ${Colors.DarkGray};
    font-weight: ${FontWeights.Bold};
  }

  span {
    color: ${Colors.DarkerGray};

    small {
      margin-left: 5px;
      position: relative;
      bottom: 2px;
      font-size: 9px;
      color: ${Colors.DarkRed};
    }
  }
`

const RightChevronStyled = styled(RightChevron)`
  height: 14px;
`
